import { View, Text, styled } from 'tamagui'
import { Users, Wifi } from '@tamagui/lucide-icons'
import type { ExploreServer } from '@/types'
import { useAppStore, useServerStore } from '@/store'
import { TierBadge } from '../../common'

const Card = styled(View, {
  backgroundColor: '$surfaceRaised',
  borderRadius: '$borderRadiusXl',
  borderWidth: 1,
  borderColor: '$borderDefault',
  padding: 16,
  gap: 10,
  // @ts-expect-error -- web transition
  style: {
    transition: 'border-color 150ms ease',
  },
  hoverStyle: {
    borderColor: '$accent',
  },
})

const HeaderRow = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 12,
})

const IconCircle = styled(View, {
  width: 40,
  height: 40,
  borderRadius: '$borderRadiusXl',
  alignItems: 'center',
  justifyContent: 'center',
})

const ServerName = styled(Text, {
  fontFamily: '$fontHeading',
  fontWeight: '600',
  fontSize: 15,
  color: '$surfaceForeground',
  flex: 1,
})

const Description = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 13,
  color: '$sidebarMuted',
  lineHeight: 18,
})

const MetaRow = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 16,
})

const MetaItem = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 4,
})

const MetaText = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 12,
  color: '$color8',
})

const TagRow = styled(View, {
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: 6,
})

const Tag = styled(View, {
  backgroundColor: '$sidebarActive',
  borderRadius: '$borderRadiusSm',
  paddingHorizontal: 8,
  paddingVertical: 3,
})

const TagText = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 11,
  color: '$color8',
})

const JoinButton = styled(View, {
  height: 32,
  borderRadius: '$borderRadiusMd',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '$accent',
  cursor: 'pointer',
  hoverStyle: {
    opacity: 0.9,
  },
  // @ts-expect-error -- web transition
  style: { transition: 'opacity 100ms ease' },
})

const VerifiedBadge = styled(View, {
  backgroundColor: '$accent',
  borderRadius: '$borderRadiusSm',
  paddingHorizontal: 6,
  paddingVertical: 2,
})

interface ExploreServerCardProps {
  server: ExploreServer
}

export function ExploreServerCard({ server }: ExploreServerCardProps) {
  const { navigateToServer, setActiveChannel } = useAppStore()
  const { addServer, servers } = useServerStore()
  const alreadyJoined = !!servers[server.id]

  const handleJoin = () => {
    if (alreadyJoined) return

    addServer({
      id: server.id,
      name: server.name,
      iconText: server.iconText,
      iconColor: server.iconColor,
      categories: [
        { id: `${server.id}-cat0`, name: 'General', collapsed: false },
      ],
      channels: [
        { id: `${server.id}-ch0`, name: 'general', type: 'text', categoryId: `${server.id}-cat0`, topic: server.description, unreadCount: 0, mentionCount: 0 },
        { id: `${server.id}-ch1`, name: 'Lounge', type: 'voice', categoryId: `${server.id}-cat0`, unreadCount: 0, mentionCount: 0 },
      ],
      memberCount: server.memberCount + 1,
      unreadCount: 0,
      mentionCount: 0,
    })

    navigateToServer(server.id)
    setActiveChannel(`${server.id}-ch0`)
  }

  return (
    <Card>
      <HeaderRow>
        <IconCircle backgroundColor={server.iconColor}>
          <Text fontFamily="$fontHeading" fontWeight="600" fontSize={14} color="$surface">
            {server.iconText}
          </Text>
        </IconCircle>
        <ServerName numberOfLines={1}>{server.name}</ServerName>
        {server.tier && <TierBadge tier={server.tier} size="sm" />}
        {server.verified && (
          <VerifiedBadge>
            <Text fontFamily="$fontBody" fontSize={10} fontWeight="600" color="$surface">✓</Text>
          </VerifiedBadge>
        )}
      </HeaderRow>

      <Description numberOfLines={2}>{server.description}</Description>

      <MetaRow>
        <MetaItem>
          <Users size={12} color="$color8" />
          <MetaText>{server.memberCount.toLocaleString()}</MetaText>
        </MetaItem>
        <MetaItem>
          <Wifi size={12} color="$success" />
          <MetaText>{server.onlineCount.toLocaleString()} online</MetaText>
        </MetaItem>
      </MetaRow>

      <TagRow>
        {server.tags.slice(0, 4).map((tag) => (
          <Tag key={tag}>
            <TagText>{tag}</TagText>
          </Tag>
        ))}
      </TagRow>

      <JoinButton onPress={handleJoin}>
        <Text fontFamily="$fontBody" fontWeight="600" fontSize={13} color="$surface">
          {alreadyJoined ? 'Joined' : 'Join Server'}
        </Text>
      </JoinButton>
    </Card>
  )
}
